import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { Menu, MenuItem, IconButton, Tooltip, Divider } from '@mui/material';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import styles from '../../styles/menu/GenericActionMenu.module.scss';

// The general-purpose GenericActionMenu component
// Props:
// - menuItems: Array of menu items with { name, icon, action, disabled? }
// - position: "left" | "right" (default: "right") - Controls menu alignment
// - icon: Optional custom icon component to use as trigger (default: EllipsisVerticalIcon)
// - iconClassName: Optional additional className for the icon
// - onToggle: Optional callback when menu opens/closes
// - externalTrigger: Optional ref to external trigger element
const GenericActionMenu = forwardRef(({ menuItems, position = "right", icon, iconClassName, onToggle, externalTrigger }, ref) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    if (onToggle) onToggle(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (onToggle) onToggle(false);
  };

  // Expose click handler for external triggers
  useImperativeHandle(ref, () => ({
    triggerClick: (event) => {
      handleClick(event);
    }
  }));

  // Split menuItems into non-delete and delete for divider
  const normalItems = menuItems.filter(item => item.name !== "Delete");
  const deleteItems = menuItems.filter(item => item.name === "Delete");

  // Use custom icon if provided, otherwise use default EllipsisVerticalIcon
  const TriggerIcon = icon || EllipsisVerticalIcon;

  return (
    <div className={styles['action-menu-container']}>
      <Tooltip title="Actions">
        <IconButton
          onClick={handleClick}
          className={styles['action-menu-trigger']}
          size="small"
        >
          <TriggerIcon className={`icon ${iconClassName || ''}`} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={externalTrigger ? externalTrigger.current : anchorEl}
        open={open}
        onClose={handleClose}
        elevation={0}
        anchorOrigin={{ 
          vertical: "bottom", 
          horizontal: position 
        }}
        transformOrigin={{ 
          vertical: "top", 
          horizontal: position 
        }}
        classes={{
          paper: styles.dropdownPaper,
          list: styles.menuList,
        }}
      >
        {[...normalItems, ...(deleteItems.length ? ["__divider__"] : []), ...deleteItems].map((item, index) => {
          if (item === "__divider__") {
            return (
              <Divider
                key={`divider-${index}`}
                className={styles['action-menu-divider']}
              />
            );
          }

          const isDelete = item.name === 'Delete';

          return (
            <MenuItem
              key={index}
              onClick={() => { item.action(); handleClose(); }}
              disabled={item.disabled}
              className={`${styles['action-menu-item']} ${
                item.disabled ? styles['action-menu-item--disabled'] : ''
              } ${isDelete ? styles['action-menu-item--delete'] : ''}`}
            >
              <span className={styles['action-menu-icon']}>
                {item.icon}
              </span>
              <span className={styles['action-menu-text']}>
                {item.name}
              </span>
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
});

export default GenericActionMenu;
